import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Grid,
  Button,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import moment from "moment"; // For date formatting
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import { DivisionsByIdDropdown } from "../../CourseCatalog/DropDowns";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

// Helper to convert MM/DD/YYYY or other valid date string to YYYY-MM-DD for date input
const formatDateForInput = (dateString) => {
  if (!dateString || typeof dateString !== "string") return "";
  // Try parsing with moment, accepting common formats, then output YYYY-MM-DD
  const mDate = moment(dateString, ["MM/DD/YYYY", "YYYY-MM-DD"], true);
  if (mDate.isValid()) {
    return mDate.format("YYYY-MM-DD");
  }
  return ""; // Or original string if preferred, or handle error
};

const Edit = ({ open, onClose, programDetail, onRecordUpdated }) => {
  // Renamed termDetail to programDetail
  const { updateData, deleteData } = useFirestore("PathwaysPrograms"); // Specify collection
  const [formData, setFormData] = useState({
    id: "", // To store Firestore document ID
    PathwayID: "",
    PathwayName: "",
    PathwayNameShort: "",
    PathwayAbbrev: "",
    Career_Field_Cd: "",
    DivisionID: "",
    SCCDirector: "",
    SCCProgramMgr: "",
    SCCLiason: "",
    HEIProgramName: "",
    Notes: "",
    Start_Date: "",
    End_Date: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (programDetail) {
      setFormData({
        id: programDetail.id || "",
        PathwayID: programDetail.PathwayID || "",
        PathwayName: programDetail.PathwayName || "",
        PathwayNameShort: programDetail.PathwayNameShort || "",
        PathwayAbbrev: programDetail.PathwayAbbrev || "",
        Career_Field_Cd: programDetail.Career_Field_Cd || "",
        DivisionID: programDetail.DivisionID || "",
        SCCDirector: programDetail.SCCDirector || "",
        SCCProgramMgr: programDetail.SCCProgramMgr || "",
        SCCLiason: programDetail.SCCLiason || "",
        HEIProgramName: programDetail.HEIProgramName || "",
        Notes: programDetail.Notes || "",
        Start_Date: formatDateForInput(programDetail.Start_Date),
        End_Date: formatDateForInput(programDetail.End_Date),
      });
    } else {
      // Reset form if programDetail is null
      setFormData({
        id: "",
        PathwayID: "",
        PathwayName: "",
        PathwayNameShort: "",
        PathwayAbbrev: "",
        Career_Field_Cd: "",
        DivisionID: "",
        SCCDirector: "",
        SCCProgramMgr: "",
        SCCLiason: "",
        HEIProgramName: "",
        Notes: "",
        Start_Date: "",
        End_Date: "",
      });
    }
  }, [programDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (!formData.id) {
        throw new Error("Document ID is missing for update.");
      }
      await updateData(formData.id, formData); // Pass ID and data separately
      setLoading(false);
      setSnackbarMessage("Record has been updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
      onClose(); // Close dialog on success
    } catch (error) {
      console.error("Error updating record:", error);
      setLoading(false);
      setSnackbarMessage(`Error updating record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (!programDetail || !programDetail.id) {
        throw new Error("Document ID is missing for delete.");
      }
      await deleteData(programDetail.id);
      setLoading(false);
      setSnackbarMessage("Record has been deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
      onClose(); // Close the main dialog
    } catch (error) {
      console.error("Error deleting record:", error);
      setLoading(false);
      setSnackbarMessage(`Error deleting record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    handleConfirmDeleteClose();
    handleDelete();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Pathway Program
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Pathway ID"
                name="PathwayID"
                value={formData.PathwayID}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Pathway Name"
                name="PathwayName"
                value={formData.PathwayName}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Pathway Name Short"
                name="PathwayNameShort"
                value={formData.PathwayNameShort}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Pathway Abbreviation"
                name="PathwayAbbrev"
                value={formData.PathwayAbbrev}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Career Field Code"
                name="Career_Field_Cd"
                value={formData.Career_Field_Cd}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DivisionsByIdDropdown
                value={formData.DivisionID}
                onChange={handleChange}
                name="DivisionID"
                label="Division"
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="SCC Director"
                name="SCCDirector"
                value={formData.SCCDirector}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="SCC Program Manager"
                name="SCCProgramMgr"
                value={formData.SCCProgramMgr}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="SCC Liason"
                name="SCCLiason"
                value={formData.SCCLiason}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="HEI Program Name"
                name="HEIProgramName"
                value={formData.HEIProgramName}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Notes"
                name="Notes"
                value={formData.Notes}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date"
                name="Start_Date"
                value={formData.Start_Date}
                onChange={handleChange}
                fullWidth
                margin="normal"
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date"
                name="End_Date"
                value={formData.End_Date}
                onChange={handleChange}
                fullWidth
                margin="normal"
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleConfirmDeleteOpen}
          deleting={loading}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleConfirmDelete}
        deleting={loading}
        message="Are you sure you want to delete this?"
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
